import { useEffect, useRef } from 'react';
import { Outlet, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useIsAuthenticated } from '@/hooks/redux';
import { authApi } from '@/store/api/authApi';
import { setToken } from '@/store/slices/authSlice';
import { getErrorMessage, showToastMessage } from '@/utils';

const agentUrl = import.meta.env.VITE_AGENT_URL;

export function AuthLayout() {
  const isAuthenticated = useIsAuthenticated();
  // const transactionCode = localStorage.getItem('transactionCode');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const code = searchParams.get('code');
  const exchangedCode = useRef<string | null>(null);

  // The "Login as" code hand-off only applies on the agent panel - the admin
  // panel must never accept it, even if someone pastes/replays such a link there.
  const isAgentPanel = window.location.origin + location.pathname === agentUrl;
  const loginAsCode = isAgentPanel ? code : null;

  // "Login as" hand-off: a code in the URL always wins, even if an agent is already
  // logged in - the fresh token replaces whatever session is currently active.
  //
  // Dispatched directly (not via useLazyQuery) - the lazy-query hook auto-unsubscribes
  // its previous request on every effect remount, and combined with StrictMode's
  // double-invoked effects + keepUnusedDataFor: 0, that purges the cached response
  // before the fetch resolves, so unwrap() reads back undefined even on a 200 OK.
  useEffect(() => {
    if (code && !isAgentPanel) {
      showToastMessage('error', 'Login links are only valid on the agent panel.');
      setSearchParams({}, { replace: true });
      return;
    }

    if (!loginAsCode || exchangedCode.current === loginAsCode) return;
    exchangedCode.current = loginAsCode;

    (async () => {
      try {
        const res = await dispatch(
          authApi.endpoints.verifyLoginToken.initiate({ token: loginAsCode })
        ).unwrap();
        if (res?.accessToken) {
          dispatch(setToken(res.accessToken));
          navigate('/', { replace: true });
        } else {
          showToastMessage('error', 'Invalid or expired login link.');
          setSearchParams({}, { replace: true });
        }
      } catch (error) {
        showToastMessage('error', getErrorMessage(error));
        setSearchParams({}, { replace: true });
      }
    })();
  }, [code, loginAsCode, isAgentPanel, dispatch, navigate, setSearchParams]);

  if (loginAsCode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  //   if (isAuthenticated && transactionCode) {
  //   return <Navigate to="/transaction-code" replace />;
  // } else if (isAuthenticated && !transactionCode) {
  //   return <Navigate to="/dashboard" replace />;
  // }

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-[#04070f] via-[#072C50] to-[#020509] px-4 py-10">
      {/* Decorative colorful animated glows */}
      <div className="pointer-events-none absolute -top-32 -left-24 w-96 h-96 rounded-full bg-[#6654E6]/30 blur-3xl animate-blob" />
      <div className="pointer-events-none absolute top-1/3 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#2E70E8]/25 blur-3xl animate-blob [animation-delay:2s]" />
      <div className="pointer-events-none absolute -bottom-40 left-1/4 w-[26rem] h-[26rem] rounded-full bg-[#EAAD07]/20 blur-3xl animate-blob [animation-delay:4s]" />
      <div className="pointer-events-none absolute top-1/4 right-1/4 w-72 h-72 rounded-full bg-[#FEC733]/15 blur-3xl animate-blob [animation-delay:6s]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.06),transparent_60%)]" />

      {/* Subtle grid overlay */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:44px_44px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,black,transparent)]" />

      {/* Twinkling accent dots */}
      <span className="pointer-events-none absolute top-[18%] left-[20%] w-1.5 h-1.5 rounded-full bg-[#EAAD07] animate-twinkle" />
      <span className="pointer-events-none absolute top-[30%] right-[22%] w-1 h-1 rounded-full bg-[#2E70E8] animate-twinkle [animation-delay:1s]" />
      <span className="pointer-events-none absolute bottom-[24%] left-[16%] w-1 h-1 rounded-full bg-[#FEC733] animate-twinkle [animation-delay:1.6s]" />
      <span className="pointer-events-none absolute bottom-[16%] right-[18%] w-1.5 h-1.5 rounded-full bg-[#6654E6] animate-twinkle [animation-delay:0.6s]" />

      <div className="relative z-10 w-full max-w-[460px] animate-fade-in">
        <Outlet />
      </div>

      <p className="relative z-10 mt-8 text-center text-xs text-white/30">
        © {new Date().getFullYear()} All rights reserved.
      </p>
    </div>
  );
}
